import { useRef, useCallback } from 'react';

/**
 * Hook para coordinar scroll con Radix Accordion de forma predecible y estable
 * 
 * Resuelve los problemas comunes de:
 * - Timing incorrecto (scrollear antes de que termine la animación)
 * - Múltiples responsables de scroll compitiendo
 * - Cálculos de posición desactualizados
 * - Flags frágiles con setTimeout/requestAnimationFrame
 * 
 * @param {Object} options - Configuración del scroll
 * @param {string} options.containerId - ID del contenedor con scroll (default: 'scrollable-modules-wrapper')
 * @param {number} options.stickyOffset - Altura de la barra sticky superior en px (default: 0)
 * @param {number} options.additionalOffset - Offset adicional para spacing en px (default: 0)
 * 
 * @returns {Object} Métodos para controlar el scroll
 * @returns {Function} scheduleScroll - Programa un scroll para después de la animación del Accordion
 * @returns {Function} executeScheduledScroll - Ejecuta el scroll pendiente (llamar desde onAnimationEnd)
 * @returns {Function} cancelScheduledScroll - Cancela cualquier scroll pendiente
 * @returns {Function} scrollImmediate - Scroll inmediato (para recursos cuando el módulo ya está abierto)
 * @returns {Function} hasPendingScroll - Verifica si hay un scroll pendiente
 * 
 * @example
 * // En ModuleContentRowComponent
 * const { scheduleScroll, executeScheduledScroll } = useAccordionScroll({
 *   containerId: 'scrollable-modules-wrapper',
 *   stickyOffset: contentFixedBar || 0
 * });
 * 
 * const handleValueChange = (value) => {
 *   const willOpen = value === 'item-1';
 *   setIsOpen(willOpen);
 *   if (willOpen) scheduleScroll(rowRef.current);
 * };
 * 
 * const handleAnimationEnd = () => {
 *   executeScheduledScroll();
 * };
 * 
 * @example
 * // En ResourceLabelComponent
 * const { scrollImmediate } = useAccordionScroll({
 *   containerId: 'scrollable-modules-wrapper',
 *   stickyOffset: contentFixedBar || 0,
 *   additionalOffset: 200
 * });
 * 
 * useEffect(() => {
 *   if (isCurrentResource && isModuleOpen) {
 *     scrollImmediate(resourceRef.current);
 *   }
 * }, [isCurrentResource, isModuleOpen]);
 */
export function useAccordionScroll({ 
  containerId = 'scrollable-modules-wrapper',
  stickyOffset = 0,
  additionalOffset = 0 
} = {}) {
  // Flag para indicar si hay un scroll pendiente
  const scrollPendingRef = useRef(false);
  
  // Referencia al elemento target del scroll pendiente
  const scrollTargetRef = useRef(null);

  /**
   * Calcula la posición de scroll para un elemento dentro del contenedor
   * 
   * Usa getBoundingClientRect justo antes de scrollear para evitar
   * cálculos desactualizados si el DOM cambió.
   * 
   * @param {HTMLElement} element - Elemento al que hacer scroll
   * @param {HTMLElement} container - Contenedor con scroll
   * @returns {number|null} Posición de scroll calculada o null si hay error
   */
  const calculateScrollPosition = useCallback((element, container) => {
    if (!element || !container) return null;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    
    // Posición relativa del elemento respecto al contenedor
    const relativeTop = elementRect.top - containerRect.top;
    
    // Posición absoluta considerando el scroll actual y offsets
    const scrollPosition = container.scrollTop + relativeTop - stickyOffset - additionalOffset;
    
    // Asegurar que no sea negativo
    return Math.max(0, scrollPosition);
  }, [stickyOffset, additionalOffset]);

  /**
   * Ejecuta el scroll de forma segura al elemento especificado
   * 
   * @param {HTMLElement} element - Elemento al que hacer scroll
   */
  const executeScroll = useCallback((element) => {
    const container = document.getElementById(containerId);
    if (!container || !element) {
      console.warn('[useAccordionScroll] Container or element not found', { containerId, element });
      return;
    }

    const scrollPosition = calculateScrollPosition(element, container);
    if (scrollPosition === null) return;

    container.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    });
  }, [containerId, calculateScrollPosition]);

  /**
   * Programa un scroll para ejecutarse después de la animación del Accordion
   * 
   * Debe llamarse ANTES de que el Accordion comience a animarse.
   * El scroll se ejecutará cuando se llame a executeScheduledScroll().
   * 
   * @param {HTMLElement} element - Elemento al que hacer scroll
   */
  const scheduleScroll = useCallback((element) => {
    if (!element) {
      console.warn('[useAccordionScroll] Attempted to schedule scroll to null element');
      return;
    }
    
    scrollPendingRef.current = true;
    scrollTargetRef.current = element;
  }, []);

  /**
   * Ejecuta el scroll pendiente programado con scheduleScroll()
   * 
   * Debe llamarse desde el handler onAnimationEnd del Accordion.Content
   * para asegurar que la animación terminó antes de scrollear.
   */
  const executeScheduledScroll = useCallback(() => {
    if (!scrollPendingRef.current) {
      return;
    }
    
    scrollPendingRef.current = false;
    const target = scrollTargetRef.current;
    scrollTargetRef.current = null;

    if (target) {
      // requestAnimationFrame asegura que el DOM se actualizó completamente
      // después de que terminó la animación del Accordion
      requestAnimationFrame(() => {
        executeScroll(target);
      });
    }
  }, [executeScroll]);

  /**
   * Cancela cualquier scroll pendiente
   * 
   * Útil cuando el usuario hace scroll manual o cuando cambia el contexto
   * y ya no es necesario ejecutar el scroll programado.
   */
  const cancelScheduledScroll = useCallback(() => {
    scrollPendingRef.current = false;
    scrollTargetRef.current = null;
  }, []);

  /**
   * Ejecuta un scroll inmediato sin esperar animaciones
   * 
   * Útil para scrollear a recursos cuando el módulo ya está abierto
   * y no hay animación de Accordion en progreso.
   * 
   * @param {HTMLElement} element - Elemento al que hacer scroll
   */
  const scrollImmediate = useCallback((element) => {
    if (!element) {
      console.warn('[useAccordionScroll] Attempted immediate scroll to null element');
      return;
    }
    
    // Usar requestAnimationFrame para asegurar que el DOM está actualizado
    // (por ejemplo, después de un cambio de estado que actualiza clases CSS)
    requestAnimationFrame(() => {
      executeScroll(element);
    });
  }, [executeScroll]);

  /**
   * Verifica si hay un scroll pendiente de ejecutar
   * 
   * @returns {boolean} true si hay un scroll pendiente
   */
  const hasPendingScroll = useCallback(() => {
    return scrollPendingRef.current;
  }, []);

  return {
    scheduleScroll,
    executeScheduledScroll,
    cancelScheduledScroll,
    scrollImmediate,
    hasPendingScroll
  };
}
